import { usePageRefresh } from '../../../../../context/PageRefreshContext';
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from '@iconify/react';
import {
    getSocietyBanks,
    createSocietyBank,
    updateSocietyBank,
    deleteSocietyBank,
} from "../../../../../redux/actions/SocietyPortal/SocietyBankAction/SocietyBankAction";
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import Breadcrumb from "../../../../../components/Breadcrumb";
import BankAccountModal from "./BankAccountModal";

const BankAccounts = () => {
    const dispatch = useDispatch();
    const { banksData, banksPagination, loading, createLoading, updateLoading, deleteLoading } = useSelector(state => state.SocietyBankReducer || {});

    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        account_category: "SAVING",
        bank_name: "",
        branch_name: "",
        account_number: "",
        ifsc_code: "",
        account_holder_name: "",
        is_default: 0,
    });
    const [page, setPage] = useState(1);

    const societyId = selectedSocietyStorer.getSocietyId();

    // Fetch bank accounts
    useEffect(() => {
        if (societyId) {
            dispatch(getSocietyBanks({ society_id: societyId, page, limit: 20 }));
        }
    }, [dispatch, societyId, page]);

    const refreshList = () => {
        dispatch(getSocietyBanks({ society_id: societyId, page, limit: 20 }));
    };
    usePageRefresh(refreshList);

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            society_id: Number(societyId),
            is_default: formData.is_default ? 1 : 0,
            onSuccess: () => {
                handleCloseModal();
                refreshList();
            }
        };

        if (editId) {
            dispatch(updateSocietyBank({ id: editId, ...payload }));
        } else {
            dispatch(createSocietyBank(payload));
        }
    };

    const handleEdit = (item) => {
        setFormData({
            account_category: item.account_category || "SAVING",
            bank_name: item.bank_name || "",
            branch_name: item.branch_name || "",
            account_number: item.account_number || "",
            ifsc_code: item.ifsc_code || "",
            account_holder_name: item.account_holder_name || "",
            is_default: item.is_default || 0,
        });
        setEditId(item.id);
        setShowModal(true);
    };

    const handleDelete = (item) => {
        if (window.confirm(`Are you sure you want to delete "${item.bank_name}"? This will also deactivate the associated GL account.`)) {
            dispatch(deleteSocietyBank({
                id: item.id,
                society_id: societyId,
                onSuccess: () => refreshList()
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            account_category: "SAVING",
            bank_name: "",
            branch_name: "",
            account_number: "",
            ifsc_code: "",
            account_holder_name: "",
            is_default: 0,
        });
        setEditId(null);
    };

    const handleSetDefault = (item) => {
        dispatch(updateSocietyBank({
            id: item.id,
            society_id: Number(societyId),
            is_default: 1,
            onSuccess: () => refreshList()
        }));
    };

    const accounts = banksData || [];

    return (
        <div className="invoice-setup-page">
            {/* No trail: "Setup" is a menu group with no page of its own, so the
                crumb led to the portal root and bounced to the login page. */}
            <Breadcrumb
                headingTitle="Bank Accounts"
                description="Manage society bank accounts for receipt processing"
                showTrail={false}
            />

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-16 bg-primary-50">
                <div className="card-body p-12 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:bank" className="text-primary-600 text-base" />
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> GL codes are auto-generated when you add a bank account. Mark one bank as default for receipt processing. Cash payments use GL 01-01-01-01 automatically.
                    </p>
                </div>
            </div>

            {/* Actions */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-12">
                <button
                    onClick={() => setShowModal(true)}
                    className="btn d-flex align-items-center gap-2 px-10 py-4 radius-6 text-white"
                    style={{ backgroundColor: '#f59e0b' }}
                    disabled={loading}
                >
                    <Icon icon="mdi:plus-circle" className="text-base" />
                    Add Bank Account
                </button>
            </div>

            {/* Loading */}
            {loading && (
                <div className="text-center py-40">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            {/* Bank Accounts Table */}
            {!loading && (
                <div>
                    {accounts.length === 0 && (
                        <div className="card radius-8 border">
                            <div className="card-body p-24 text-center">
                                <Icon icon="mdi:bank-off" className="text-2xl text-secondary-light mb-12" />
                                <p className="text-xs text-secondary-light mb-0">No bank accounts found. Click "Add Bank Account" to get started.</p>
                            </div>
                        </div>
                    )}

                    <div className="table-responsive">
                        {accounts.length > 0 && (
                            <table className="table table-hover align-middle">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">#</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Bank Name</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Category</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Branch</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Account Number</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">IFSC</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">GL Account</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Default</th>
                                        <th className="pe-20 py-8 text-secondary-light text-xs fw-semibold text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="ps-12 py-8">{index + 1}</td>
                                            <td className="py-16">
                                                <div className="fw-medium">{item.bank_name}</div>
                                                {item.account_holder_name && (
                                                    <div className="text-xs text-secondary-light">{item.account_holder_name}</div>
                                                )}
                                            </td>
                                            <td className="py-16">
                                                <span className={`badge fw-medium px-8 py-4 radius-4 text-xs ${item.account_category === 'CURRENT' ? 'bg-info-50 text-info-600' : 'bg-success-50 text-success-600'}`}>
                                                    {item.account_category || '-'}
                                                </span>
                                            </td>
                                            <td className="py-8 text-secondary-light">{item.branch_name || '-'}</td>
                                            <td className="py-16">
                                                <span className="font-monospace text-sm">{item.account_number || '-'}</span>
                                            </td>
                                            <td className="py-16">
                                                <span className="font-monospace text-sm">{item.ifsc_code || '-'}</span>
                                            </td>
                                            <td className="py-16">
                                                {item.gl_code ? (
                                                    <div>
                                                        <span className="badge bg-primary-50 text-primary-600 fw-medium px-8 py-4 radius-4 text-xs">
                                                            {item.gl_code}
                                                        </span>
                                                        {item.glAccount?.name && (
                                                            <div className="text-xs text-secondary-light mt-2">{item.glAccount.name}</div>
                                                        )}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td className="py-16">
                                                {item.is_default === 1 ? (
                                                    <span className="badge bg-success-50 text-success-600 fw-medium px-10 py-4 radius-4">
                                                        <Icon icon="mdi:check-circle" className="me-1" /> Default
                                                    </span>
                                                ) : (
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary px-8 py-3 radius-4 text-xs"
                                                        onClick={() => handleSetDefault(item)}
                                                        disabled={updateLoading}
                                                    >
                                                        Set Default
                                                    </button>
                                                )}
                                            </td>
                                            <td className="pe-12 py-8 text-end">
                                                <div className="d-flex align-items-center justify-content-end gap-2">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary px-8 py-4 radius-4"
                                                        onClick={() => handleEdit(item)}
                                                        title="Edit"
                                                    >
                                                        <Icon icon="mdi:pencil" />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger px-8 py-4 radius-4"
                                                        onClick={() => handleDelete(item)}
                                                        title="Delete"
                                                        disabled={deleteLoading}
                                                    >
                                                        <Icon icon="mdi:delete" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {banksPagination && banksPagination.totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-16">
                            <span className="text-xs text-secondary-light">
                                Showing page {banksPagination.page} of {banksPagination.totalPages} ({banksPagination.total} total)
                            </span>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage(p => p - 1)}
                                >
                                    Previous
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page >= banksPagination.totalPages}
                                    onClick={() => setPage(p => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            <BankAccountModal
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                editId={editId}
                loading={createLoading || updateLoading}
            />
        </div>
    );
};

export default BankAccounts;
